// Copyright TraderEvolution Global LTD. © 2017-2024. All rights reserved.

import { InstrumentUtils } from "../../Utils/Instruments/InstrumentUtils.js";
import { ProductType } from "../../Utils/Instruments/ProductType.js";
import { QuoteValid } from "../../Utils/Quotes/QuoteValid.js";
import { DateTimeConvertor } from "../../Utils/Time/DateTimeConvertor.js";
import { OperationType } from "../../Utils/Trading/OperationType.ts";
import { DataCache } from "../DataCache.js";
import { SessionSettings } from "../SessionSettings.js";

export let PositionsBalance = function (position, accsToUse)
{
    if (!position)
        return

    this.rowID = null;

    let ins = position.Instrument
    if (!ins) return

    this.Instrument = ins;
    this.Position = position;

    this.Symbol = position.GetInstrumentDisplayName();   // zero column
    this.NetPositionQty = null;        // always in lots
    this.BreakEvenPoint = null;
    this.CurrentPrice = null;
    this.GrossPL = null;
    this.LongQty = null;               // 5
    this.AverageLong = null;
    this.ShortQty = null;
    this.AverageShort = null;
    this.GrossExposure = null;
    this.NetExposure = null;           // 10
    this.StrikePrice = null;
    this.ExpDate = null;
    this.PositionValue = null;
    this.isCorporateAction = position.ProductType === ProductType.CorporateAction;

    this._accsToUse = accsToUse;

    this.isEmpty = accsToUse ? this.RecalculateByAccounts(accsToUse) : this.RecalculateByInstrument(ins)
}

PositionsBalance.prototype.RecalculateByAccounts = function (accsInUse)
{
    return this.RecalculateByInstrument(this.Instrument, accsInUse)
}

PositionsBalance.prototype.RecalculateByInstrument = function (ins, accToUse, aggregateTradableID)
{
    let UseAccCurrency = DataCache.getUserBaseCurrency() != DataCache.baseCurrency;

    let profit = 0,
        exposure = 0, exposureNet = 0,
        positionValue = 0,
        everBuy = 0, everSell = 0,
        buyAmount = 0, sellAmount = 0,
        isEmpty = true,
        amount = 0

    let tID = InstrumentUtils.RemoveRouteName(ins.GetInteriorID()),
        routeID = ins.getRoute(),
        usedRoutes = [],
        routesArr = aggregateTradableID ? ins.RoutesSupported : [routeID],
        exposurePart = 1

    for (let i = 0; i < routesArr.length; i++)
    {
        let instrument = aggregateTradableID ? DataCache.getInstrumentByName(InstrumentUtils.GetFullName(tID, routesArr[i])) : ins,
            positions = DataCache.getPositionsByInstrument(instrument, this.isCorporateAction)

        for (let posId in positions)
        {
            let pos = positions[posId],
                isBuy = pos.BuySell == OperationType.Buy

            if (pos.isTradePosition || (accToUse && !accToUse[pos.Account.AcctNumber]))
                continue

            let tickCost = this.Instrument.GetTickCost(),
                posExpositionValue = pos.getExpositionValue(UseAccCurrency)
            exposurePart = UseAccCurrency ? pos.OpenCrossPrice : DataCache.CrossRateCache.GetCrossPriceIns(this.Instrument) * tickCost

            isEmpty = false
            profit += pos.getProfitValue(UseAccCurrency)
            exposure += posExpositionValue
            positionValue += pos.getPositionValue(UseAccCurrency)
            amount = pos.Amount // ALWAYS IN LOTS

            if (isBuy)
            {
                buyAmount += amount
                everBuy += pos.Price * amount
                exposureNet += posExpositionValue / exposurePart
            }
            else
            {
                sellAmount += amount;
                everSell += pos.Price * amount;
                exposureNet -= posExpositionValue / exposurePart;
            }
        }
    }

    this.NetPositionQty = buyAmount - sellAmount
    this.AverageLong = (buyAmount > 0) ? everBuy / buyAmount : 0;
    this.AverageShort = (sellAmount > 0) ? everSell / sellAmount : 0;

    this.BreakEvenPoint = (this.NetPositionQty != 0) ? (buyAmount * this.AverageLong - sellAmount * this.AverageShort) / (this.NetPositionQty) : -1; //#43665
    if (this.BreakEvenPoint < 0 && this.BreakEvenPoint != -1)
        this.BreakEvenPoint = -1; //признак невалидного значения

    this.CurrentPrice = PositionsBalance.GetCurrentPrice(ins, buyAmount, sellAmount);
    this.GrossPL = isNaN(this.CurrentPrice) ? NaN : profit;
    this.LongQty = buyAmount || 0;
    this.ShortQty = sellAmount || 0;
    this.NetExposure = exposureNet * exposurePart;
    this.GrossExposure = exposure;
    this.StrikePrice = (ins != null && ins.StrikePrice > 0) ? ins.StrikePrice : 0;
    this.ExpDate = (ins != null && ins.ExpDate) ? DateTimeConvertor.ConvertUTCTimeToSelectedTimeZone(new Date(ins.ExpDateReal.getTime()), ins.DataCache) : null
    this.PositionValue = positionValue;

    return isEmpty
}


PositionsBalance.GetCurrentPrice = function (instrument, buyAmount, sellAmount)
{
    if (instrument == null || instrument.DataCache == null)
        return -1;

    if (instrument.CalcPriceOnTrade())
    {
        // let q3Msg = instrument.DataCache.QuoteController.GetLastTrade(instrument.InstrumentDescription);
        // if (q3Msg != null)
        //     return q3Msg.Price;

        let lastPrice = instrument.Level1.GetLastPrice();
        if (isNaN(lastPrice) || lastPrice >= 0)
            return lastPrice;

        return -1;
    }
    else
    {
        let lastQuote = instrument.GetLastQuote(QuoteValid.Last);
        if (lastQuote == null)
            return -1;

        let lastPrice = 0;

        if (!lastQuote.IsValid)
            lastPrice = instrument.Level1.GetLastPrice();

        let sp = instrument.DataCache.GetSpreadPlan()
        let ask = !isNaN(lastQuote.Ask) ? lastQuote.AskSpread_SP_Ins(sp, instrument) : lastPrice;
        let bid = !isNaN(lastQuote.Bid) ? lastQuote.BidSpread_SP_Ins(sp, instrument) : lastPrice;

        //чтобы было как в позиции (#39817)
        let curPrice = (buyAmount - sellAmount > 0) ? bid : ask;
        if (curPrice == 0)
        {
            let prevClose = instrument.Level1.GetPrevClose();
            if (prevClose >= 0)
                curPrice = prevClose;
        }

        return curPrice;
    }
}

PositionsBalance.prototype.Dispose = function ()
{
    this.Account = null;
    this.InstrumentStr = null;
}

PositionsBalance.prototype.Recalc = function ()
{
    this.isEmpty = this._accsToUse ? this.RecalculateByAccounts(this._accsToUse) : this.RecalculateByInstrument(this.Instrument)
}
